// ============================================================================
// ChallanPrintingView.jsx
// "Challan Printing" card — browse every generated challan in the current
// campus, filter it by academic placement, and print one or many challans.
// The printed layout itself lives in challanDocument.js so the preview and the
// print output always match.
// ============================================================================

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import { fetchGeneratedChallans } from "./challanApi";
import {
  buildChallanData,
  challanStyles,
  printChallanSheets,
  renderChallanSheetHtml,
} from "./challanDocument";
import {
  classSessionIdsOf,
  sectionsForClassSession,
  uniqueClassNames,
} from "../../../utils/campusClasses";

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtPKR       = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK")}`;
const fmtClass     = (c) => c ? c.replace(/Grade/i, "Class").trim() : "—";
const fmtSection   = (s) => s ? s.replace(/Section\s*/i, "").trim() : "—";
const loadPrevDues = (sid) => { try { return JSON.parse(localStorage.getItem(`previous_dues_${sid}`) || "[]").filter(d => !d.paid).reduce((s, d) => s + d.amount, 0); } catch { return 0; } };

// ── Design tokens (matches ALS Portal brand palette in src/index.css) ────────
const C = {
  navy: "#0f1d36", navyMid: "#1c3461", blue: "#2a4a7f",
  blueSoft: "rgba(30,58,95,0.10)", amber: "#8a6c1f", amberSoft: "rgba(196,154,42,0.15)",
  green: "#2e7d32", greenSoft: "rgba(46,125,50,0.10)", greenBdr: "rgba(46,125,50,0.35)",
  red: "#c41e24", redSoft: "rgba(196,30,36,0.08)",
  slate: "#5a5e6a", slateLight: "#8e92a0", border: "#e8e9ed",
  bg: "#f8f9fc", white: "#ffffff", text: "#1a1d23",
};

const S = {
  card:      { background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, marginBottom: "16px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  cardHead:  { padding: "16px 20px", background: C.navy, display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: "15px", fontWeight: 700, color: C.white },
  filterRow: { padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg },
  fg:        { display: "flex", flexDirection: "column", gap: "5px", minWidth: "150px" },
  label:     { fontSize: "11px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px" },
  select:    { padding: "9px 12px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", background: C.white, cursor: "pointer", color: C.text },
  input:     { padding: "9px 12px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", background: C.white, outline: "none", color: C.text, width: "100%", boxSizing: "border-box" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { textAlign: "left", padding: "10px 16px", fontSize: "11px", fontWeight: 700, color: C.slate, background: C.bg, borderBottom: `2px solid ${C.border}`, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" },
  td:        { padding: "12px 16px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" },
  badge:     (bg, fg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: bg, color: fg }),
  btn:       (bg, fg, border) => ({ padding: "8px 16px", borderRadius: "8px", border: border || "none", background: bg, color: fg, fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }),
  overlay:   { position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)" },
  modal:     { background: C.white, borderRadius: "14px", width: "min(96vw, 1180px)", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  modalHead: { padding: "18px 24px", background: C.navy, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10, borderRadius: "14px 14px 0 0" },
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconPrint = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><path d="M6 9V3h12v6"/><rect x="6" y="15" width="12" height="6" rx="2"/></svg>;
const IconClose = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

// ── Challan Preview / Print Modal ─────────────────────────────────────────────
// Renders the exact printed sheets (three copies per challan) on screen.
const ChallanPrintModal = ({ items, onClose }) => {
  const sheets = useMemo(
    () => items.map(({ student, feeStructure, challan }) => buildChallanData({
      student,
      feeStructure,
      challan,
      previousDues: loadPrevDues(student.id),
    })),
    [items],
  );

  const documentTitle = sheets.length === 1
    ? `Fee Challan — ${sheets[0].challanNumber}`
    : `Fee Challans — ${sheets.length}`;

  const subtitle = sheets.length === 1
    ? `${sheets[0].studentName} · ${sheets[0].categoryLabel} · ${sheets[0].monthText}`
    : `${sheets.length} challans · Bank, School and Student copies per challan`;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.modalHead}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
              {sheets.length === 1 ? "Fee Challan Preview" : `Bulk Print — ${sheets.length} challans`}
            </div>
            <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "2px" }}>{subtitle}</div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => printChallanSheets(sheets, documentTitle)} style={S.btn("#fff", C.navy)}>
              <IconPrint /> {sheets.length === 1 ? "Print Challan" : "Print All"}
            </button>
            <button onClick={onClose} style={S.btn("rgba(255,255,255,0.15)", "#fff")}><IconClose /> Close</button>
          </div>
        </div>

        <div style={{ padding: "20px", background: C.bg }}>
          <style>{challanStyles}</style>
          <div
            style={{ background: C.white, borderRadius: "10px", border: `1px solid ${C.border}`, overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: sheets.map(renderChallanSheetHtml).join("") }}
          />
        </div>
      </div>
    </div>
  );
};

// ── Main ChallanPrintingView ──────────────────────────────────────────────────
const ChallanPrintingView = ({
  students = [],
  feeAssignments = {},
  selected = new Set(),
  onSelectedChange,
  onGoToGeneration,
  campusId,
}) => {
  const [printItems, setPrintItems] = useState(null);
  const [generatedChallans, setGeneratedChallans] = useState([]);
  const [campusSessions, setCampusSessions] = useState([]);
  const [campusClasses, setCampusClasses] = useState([]);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");

  const refreshChallans = useCallback(async () => {
    const records = await fetchGeneratedChallans();
    setGeneratedChallans(Array.isArray(records) ? records : []);
  }, []);

  // Sessions and classes come from the campus itself, so a student record with a
  // stale academic_session can't put a session from another campus in the list.
  useEffect(() => {
    let alive = true;
    const scope = campusId ? `?campus_id=${campusId}` : "";
    const load = (path) =>
      fetch(`${API_BASE_URL}${path}`, { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []);

    Promise.all([load(`/api/sessions${scope}`), load(`/api/classes${scope}`)]).then(
      ([sessionData, classData]) => {
        if (!alive) return;
        setCampusSessions(Array.isArray(sessionData) ? sessionData : []);
        setCampusClasses(Array.isArray(classData) ? classData : []);
      },
    );

    return () => { alive = false; };
  }, [campusId]);

  useEffect(() => { refreshChallans(); }, [refreshChallans, feeAssignments]);
  useEffect(() => {
    const handler = () => refreshChallans();
    window.addEventListener("challans-generated", handler);
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("challans-generated", handler);
      window.removeEventListener("focus", handler);
    };
  }, [refreshChallans]);

  const classById = useMemo(
    () => new Map(campusClasses.map((classItem) => [String(classItem.id), classItem])),
    [campusClasses],
  );
  const classByName = useMemo(() => {
    const map = new Map();
    campusClasses.forEach((classItem) => {
      const key = String(classItem.name || "").trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, classItem);
    });
    return map;
  }, [campusClasses]);
  const sessionNameById = useMemo(
    () => new Map(campusSessions.map((session) => [String(session.id), session.name])),
    [campusSessions],
  );
  const campusSessionNames = useMemo(
    () => campusSessions.map((session) => session.name).filter(Boolean),
    [campusSessions],
  );

  // A student row, the fee assignment and the stored challan can each be missing
  // a piece of the placement, so take the first value that actually resolves and
  // prefer a session that really exists on this campus.
  const resolvePlacement = useCallback(
    (student, feeStructure, challan) => {
      const classRecord =
        (student.class_id && classById.get(String(student.class_id))) ||
        (challan?.studentClassId && classById.get(String(challan.studentClassId))) ||
        classByName.get(String(student.class || challan?.studentClass || "").trim().toLowerCase()) ||
        classByName.get(String(student.grade || "").trim().toLowerCase()) ||
        null;

      const className =
        student.class || classRecord?.name || challan?.studentClass || "";
      const section =
        fmtSection(student.section || classRecord?.section || challan?.studentSection || "");

      const classSessionNames = classRecord
        ? classSessionIdsOf(classRecord)
            .map((sessionId) => sessionNameById.get(String(sessionId)))
            .filter(Boolean)
        : [];
      const sessionCandidates = [
        feeStructure?.session,
        challan?.session,
        ...classSessionNames,
        student.academic_session,
        feeStructure?.academicYear,
        challan?.academicYear,
      ].filter(Boolean);
      const session =
        sessionCandidates.find((candidate) => campusSessionNames.includes(candidate)) ||
        sessionCandidates[0] ||
        "";

      return { className, section, session };
    },
    [classById, classByName, sessionNameById, campusSessionNames],
  );

  const generatedItems = useMemo(() => {
    const studentMap = new Map(students.map((student) => [String(student.id), student]));

    return generatedChallans
      .map((challan) => {
        const student = studentMap.get(String(challan.studentId));
        // The students prop is already campus-scoped on the superadmin campus
        // page, so this also prevents challans from another campus appearing.
        if (!student) return null;

        const fees = feeAssignments[student.id] || feeAssignments[String(student.id)] || {};
        const assignedFee = fees[challan.feeAssignmentKey] || Object.values(fees).find(
          (fee) =>
            String(fee.categoryId) === String(challan.categoryId) &&
            String(fee.month || "") === String(challan.month || ""),
        );
        const feeStructure = {
          ...(assignedFee || {}),
          categoryId: challan.categoryId || assignedFee?.categoryId,
          categoryLabel: challan.categoryLabel || assignedFee?.categoryLabel || "Fee",
          categoryPrefix: challan.categoryPrefix || assignedFee?.categoryPrefix || "CHN",
          selectedCategories: challan.selectedCategories || assignedFee?.selectedCategories || [],
          components: challan.components || assignedFee?.components || [],
          academicYear: challan.academicYear || assignedFee?.academicYear || "",
          month: challan.month || assignedFee?.month || "",
          dueDate: challan.dueDate || assignedFee?.dueDate || "10",
          subtotal: challan.subtotal ?? assignedFee?.subtotal ?? 0,
          concessionAmount: challan.concessionAmount ?? assignedFee?.concessionAmount ?? 0,
          netPayable: challan.netPayable ?? assignedFee?.netPayable ?? 0,
          status: challan.status || assignedFee?.status || "Unpaid",
        };

        const placement = resolvePlacement(student, feeStructure, challan);

        return {
          id: `${student.id}-${feeStructure.categoryId}-${feeStructure.month || ""}`,
          // The printed challan reads class/section/session off the student, so
          // hand it the resolved values rather than the raw record.
          student: {
            ...student,
            class: placement.className,
            section: placement.section === "—" ? "" : placement.section,
            academic_session: placement.session,
          },
          feeStructure,
          challan,
          ...placement,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.challan.generationDate || 0) - new Date(a.challan.generationDate || 0));
  }, [students, feeAssignments, generatedChallans, resolvePlacement]);

  const sessions = useMemo(() => {
    const names = new Set(campusSessionNames);
    generatedItems.forEach(({ session }) => { if (session) names.add(session); });
    return [...names].sort((a, b) => String(b).localeCompare(String(a)));
  }, [campusSessionNames, generatedItems]);

  const classes = useMemo(() => {
    if (campusClasses.length) {
      const selectedSession = campusSessions.find((session) => session.name === sessionFilter);
      const names = uniqueClassNames(campusClasses, {
        sessionId: sessionFilter === "all" ? "" : selectedSession?.id,
      });
      return names;
    }

    const items = sessionFilter === "all"
      ? generatedItems
      : generatedItems.filter(({ session }) => session === sessionFilter);
    return [...new Set(items.map(({ className }) => className).filter(Boolean))].sort();
  }, [campusClasses, campusSessions, generatedItems, sessionFilter]);

  const sections = useMemo(() => {
    if (campusClasses.length) {
      const selectedSession = campusSessions.find((session) => session.name === sessionFilter);
      const values = sectionsForClassSession(campusClasses, {
        sessionId: sessionFilter === "all" ? "" : selectedSession?.id,
        className: classFilter === "all" ? "" : classFilter,
        students,
        formatSection: fmtSection,
      });
      return values;
    }

    const items = generatedItems.filter((item) =>
      (sessionFilter === "all" || item.session === sessionFilter) &&
      (classFilter === "all" || item.className === classFilter)
    );
    return [...new Set(items.map(({ section }) => section).filter((section) => section && section !== "—"))].sort();
  }, [campusClasses, campusSessions, generatedItems, sessionFilter, classFilter, students]);

  useEffect(() => {
    if (classFilter !== "all" && !classes.includes(classFilter)) {
      setClassFilter("all");
    }
  }, [classes, classFilter]);

  useEffect(() => {
    if (sectionFilter !== "all" && !sections.includes(sectionFilter)) {
      setSectionFilter("all");
    }
  }, [sections, sectionFilter]);

  const filteredItems = useMemo(() => {
    const query = searchQ.trim().toLowerCase();
    return generatedItems.filter(({ student, feeStructure, challan, className, section, session }) => {
      const matchesSession = sessionFilter === "all" || session === sessionFilter;
      const matchesClass = classFilter === "all" || className === classFilter;
      const matchesSection = sectionFilter === "all" || section === sectionFilter;
      const matchesSearch = !query || [
        student.name,
        student.rollNo,
        challan.challanNumber,
        feeStructure.categoryLabel,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      return matchesSession && matchesClass && matchesSection && matchesSearch;
    });
  }, [generatedItems, sessionFilter, classFilter, sectionFilter, searchQ]);

  const selectedItems = useMemo(
    () => filteredItems.filter((item) => selected.has(item.id)),
    [filteredItems, selected],
  );
  const itemsToBulkPrint = selectedItems.length > 0 ? selectedItems : filteredItems;
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((item) => selected.has(item.id));

  const toggleOne = (id) => onSelectedChange?.((previous) => {
    const next = new Set(previous);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => onSelectedChange?.((previous) => {
    const next = new Set(previous);
    filteredItems.forEach(({ id }) => allFilteredSelected ? next.delete(id) : next.add(id));
    return next;
  });
  const clearSelection = () => onSelectedChange?.(new Set());

  return (
    <>
      <div style={S.card}>
        <div style={S.cardHead}>
          <span style={S.cardTitle}>Challan Printing</span>
          <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.12)", padding: "3px 10px", borderRadius: "20px", fontWeight: 600 }}>
            {filteredItems.length} of {generatedItems.length} generated challans
          </span>
        </div>

        <div style={S.filterRow}>
          <div style={S.fg}>
            <label style={S.label}>Session</label>
            <select
              style={S.select}
              value={sessionFilter}
              onChange={(event) => {
                setSessionFilter(event.target.value);
                setClassFilter("all");
                setSectionFilter("all");
              }}
            >
              <option value="all">All Sessions</option>
              {sessions.map((session) => <option key={session} value={session}>{session}</option>)}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Class</label>
            <select
              style={S.select}
              value={classFilter}
              onChange={(event) => {
                setClassFilter(event.target.value);
                setSectionFilter("all");
              }}
            >
              <option value="all">All Classes</option>
              {classes.map((className) => <option key={className} value={className}>{fmtClass(className)}</option>)}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Section</label>
            <select style={S.select} value={sectionFilter} onChange={(event) => setSectionFilter(event.target.value)}>
              <option value="all">All Sections</option>
              {sections.map((section) => <option key={section} value={section}>{section}</option>)}
            </select>
          </div>
          <div style={{ ...S.fg, minWidth: "220px", flex: "1 1 220px" }}>
            <label style={S.label}>Search</label>
            <input
              style={S.input}
              value={searchQ}
              onChange={(event) => setSearchQ(event.target.value)}
              placeholder="Student, roll no, challan no..."
            />
          </div>
        </div>

        <div style={{ padding: "12px 20px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", background: C.white }}>
          <button
            onClick={() => setPrintItems(itemsToBulkPrint)}
            disabled={itemsToBulkPrint.length === 0}
            style={{ ...S.btn(C.green, "#fff"), opacity: itemsToBulkPrint.length === 0 ? 0.55 : 1, cursor: itemsToBulkPrint.length === 0 ? "not-allowed" : "pointer" }}
          >
            <IconPrint />
            {selectedItems.length > 0
              ? `Print Selected (${selectedItems.length})`
              : `Print Filtered (${filteredItems.length})`}
          </button>
          {selectedItems.length > 0 && (
            <button onClick={clearSelection} style={S.btn(C.bg, C.slate, `1px solid ${C.border}`)}>
              Clear Selection
            </button>
          )}
          <button onClick={onGoToGeneration} style={S.btn(C.bg, C.slate, `1px solid ${C.border}`)}>
            Go to Challan Generation
          </button>
        </div>
      </div>

      <div style={S.card}>
        {filteredItems.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: C.slate }}>
            <div style={{ fontSize: "36px", marginBottom: "12px" }}>🧾</div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>
              {generatedItems.length === 0 ? "No Generated Challans for This Campus" : "No Challans Match These Filters"}
            </div>
            <div style={{ fontSize: "13px" }}>
              {generatedItems.length === 0
                ? "Generated challans for students in this campus will appear here automatically."
                : "Try selecting a different session, class, section, or search term."}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "40px" }}>
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} style={{ width: 15, height: 15, accentColor: C.blue, cursor: "pointer" }} />
                  </th>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Challan No.</th>
                  <th style={S.th}>Student</th>
                  <th style={S.th}>Session</th>
                  <th style={S.th}>Class</th>
                  <th style={S.th}>Section</th>
                  <th style={S.th}>Roll No.</th>
                  <th style={S.th}>Fee Category</th>
                  <th style={S.th}>Month</th>
                  <th style={S.th}>Net Payable</th>
                  <th style={S.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, idx) => {
                  const { id: itemId, student, feeStructure, challan, className, section, session } = item;
                  const components = feeStructure.components || [];
                  const previousDues = loadPrevDues(student.id);
                  const netPayable = Number(feeStructure.netPayable || 0) + previousDues;
                  const [feeYear, feeMonthNum] = String(feeStructure.month || "").split("-");
                  const monthIndex = feeMonthNum ? parseInt(feeMonthNum, 10) - 1 : -1;
                  const displayMonth = monthIndex >= 0 ? MONTHS[monthIndex] : MONTHS[new Date().getMonth()];
                  const displayYear = feeYear || String(new Date().getFullYear());
                  const checked = selected.has(itemId);

                  return (
                    <tr key={challan.id || itemId} style={{ background: checked ? C.blueSoft : idx % 2 === 0 ? C.white : C.bg }}>
                      <td style={S.td}>
                        <input type="checkbox" checked={checked} onChange={() => toggleOne(itemId)} style={{ width: 15, height: 15, accentColor: C.blue, cursor: "pointer" }} />
                      </td>
                      <td style={{ ...S.td, color: C.slateLight, fontWeight: 600 }}>{idx + 1}</td>
                      <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 700 }}>{challan.challanNumber}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{student.name?.charAt(0)?.toUpperCase() || "?"}</div>
                          {student.name}
                        </div>
                      </td>
                      <td style={S.td}>{session || "—"}</td>
                      <td style={S.td}><span style={S.badge(C.blueSoft, C.blue)}>{fmtClass(className)}</span></td>
                      <td style={{ ...S.td, fontWeight: 600 }}>{section || "—"}</td>
                      <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 600 }}>{student.rollNo || "—"}</td>
                      <td style={S.td}><span style={S.badge(C.amberSoft, C.amber)}>{feeStructure.categoryLabel}</span></td>
                      <td style={{ ...S.td, fontWeight: 600 }}>{displayMonth} {displayYear}</td>
                      <td style={S.td}>
                        <span style={S.badge(C.greenSoft, C.green)}>{fmtPKR(netPayable)}</span>
                      </td>
                      <td style={S.td}>
                        <button
                          style={{ ...S.btn(C.navy, "#fff"), opacity: components.length === 0 ? 0.55 : 1 }}
                          disabled={components.length === 0}
                          title={components.length === 0 ? "No fee components available" : "Preview and print challan"}
                          onClick={() => setPrintItems([item])}
                        >
                          <IconPrint /> Print
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {printItems?.length > 0 && (
        <ChallanPrintModal items={printItems} onClose={() => setPrintItems(null)} />
      )}
    </>
  );
};

export default ChallanPrintingView;
